import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
    teamDetailFromRestV1,
    teamFromRestV1,
    teamsSummaryFromRestV1,
    toTeamCreateWire,
    toTeamUpdateWire,
} from 'domain/mappers/teamFromRestV1';
import type { CreateTeamInputFE, TeamDetailFE, TeamFE, TeamSummaryFE, UpdateTeamInputFE } from 'domain/systemAdmin';

import { errorHandler, formatAxiosError, formatAxiosErrorRedacted, isAxiosError } from '../errors';
import { correlationHeaders, managementRestApi } from '../restClient';

const listSystemTeamsQueryFn = async (): Promise<{ data: TeamSummaryFE[] }> => {
    try {
        const response = await managementRestApi.get<unknown>('/api/v1/teams', correlationHeaders());
        return { data: teamsSummaryFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[listTeams]: Unexpected error occurred while fetching system teams', loggableError);
    }
};

/**
 * The one team endpoint whose payload carries people, so it reports through the redacted formatter:
 * `responseData` on a failed read of a member collection is not worth logging. The other three
 * carry team names and ids only.
 */
const getSystemTeamAttributeQueryFn = async (id: string): Promise<{ data: TeamDetailFE }> => {
    try {
        const response = await managementRestApi.get<unknown>(`/api/v1/teams/${id}`, correlationHeaders());
        return { data: teamDetailFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosErrorRedacted(error) : error;
        throw errorHandler(
            `[getTeamById]: Unexpected error occurred while fetching system team ${id} attributes`,
            loggableError
        );
    }
};

const createTeamFn = async (input: CreateTeamInputFE): Promise<{ data: TeamFE }> => {
    try {
        const response = await managementRestApi.post<unknown>(
            '/api/v1/teams',
            toTeamCreateWire(input),
            correlationHeaders()
        );
        return { data: teamFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[createTeam]: Unexpected error occurred while creating team', loggableError);
    }
};

const editTeamFn = async (input: UpdateTeamInputFE): Promise<{ data: TeamFE }> => {
    try {
        const response = await managementRestApi.put<unknown>(
            `/api/v1/teams/${input.id}`,
            toTeamUpdateWire(input),
            correlationHeaders()
        );
        return { data: teamFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[updateTeam]: Unexpected error occurred while updating team', loggableError);
    }
};

export const systemTeamsApi = createApi({
    reducerPath: 'systemTeams',
    tagTypes: ['Teams'],
    // Every endpoint runs on `queryFn`, so this is never invoked — and keeping a GraphQL base
    // query on a slice that issues no GraphQL request would be misleading.
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        createTeam: builder.mutation<TeamFE, CreateTeamInputFE>({
            queryFn: createTeamFn,
            invalidatesTags: ['Teams'],
        }),
        editTeam: builder.mutation<TeamFE, UpdateTeamInputFE>({
            queryFn: editTeamFn,
            invalidatesTags: ['Teams'],
        }),
        // The `string` arg is the cache key, not an input — `'ListSystemTeams'`, unchanged from
        // the GraphQL endpoint. Do not "clean up" the unread argument.
        listSystemTeams: builder.query<TeamSummaryFE[], string>({
            queryFn: listSystemTeamsQueryFn,
            providesTags: ['Teams'],
        }),
        getSystemTeamsAttributes: builder.query<TeamDetailFE, string>({
            queryFn: getSystemTeamAttributeQueryFn,
        }),
    }),
});

export const {
    useListSystemTeamsQuery,
    useGetSystemTeamsAttributesQuery,
    useLazyListSystemTeamsQuery,
    useCreateTeamMutation,
    useEditTeamMutation,
} = systemTeamsApi;
